import {
  BusinessRelationColId,
  BusinessRelationColumnDef,
} from "constants/columnDefinitions/businessRelations";
import {
  BusinessColId,
  BusinessColumnDef,
  BusinessNameSpaceColId,
} from "constants/columnDefinitions";
import { ColDef, ColGroupDef } from "ag-grid-community";
import {
  BusinessRelationAgGridRowType,
  selectBusinessRelationsAggridData,
} from "selectors/aggrid/businessRelations";
import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import {
  useHasDeploymentPermission,
  useHasExportBusinessesPermission,
} from "hooks";
import { Column } from "components/Layout";
import AgGridTable from "components/AgGrid/AgGridContainer";
import { AgGridTables } from "constants/aggrid";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { openBusinessRelationshipHashRoute } from "lib/navigation";
import {
  createNamespacedColumnDefinitions,
  filterColIdsFromColumnDef,
} from "lib/agGrid";
import { DeploymentPermissions } from "constants/permissions";

interface GetRowIdProps {
  data: BusinessRelationAgGridRowType;
}

const getRowId = (params: GetRowIdProps): string => params.data.relationship.id;

export const LocalBusinessRelationColumnDef = {
  [BusinessRelationColId.RELATED_FROM_BUSINESS]: {
    colId: BusinessRelationColId.RELATED_FROM_BUSINESS,
    headerName: "Business",
    children: Object.values(
      createNamespacedColumnDefinitions(
        filterColIdsFromColumnDef(BusinessColumnDef, [
          BusinessColId.ACTIONS,
          BusinessColId.PROPERTIES_SUMMARY,
          BusinessColId.RELATIONSHIPS,
        ]),
        "relatedFromBusiness",
        BusinessNameSpaceColId.RELATED_FROM_BUSINESS,
      ),
    ),
  },
  [BusinessRelationColId.RELATED_TO_BUSINESS]: {
    colId: BusinessRelationColId.RELATED_TO_BUSINESS,
    headerName: "Related To",
    children: Object.values(
      createNamespacedColumnDefinitions(
        filterColIdsFromColumnDef(BusinessColumnDef, [
          BusinessColId.ACTIONS,
          BusinessColId.PROPERTIES_SUMMARY,
          BusinessColId.RELATIONSHIPS,
        ]),
        "relatedToBusiness",
        BusinessNameSpaceColId.RELATED_TO_BUSINESS,
      ),
    ),
  },
  [BusinessRelationColId.BUSINESS_RELATION]: {
    colId: BusinessRelationColId.BUSINESS_RELATION,
    headerName: "Relation",
    children: [
      BusinessRelationColumnDef[BusinessRelationColId.RELATION_TYPE],
      BusinessRelationColumnDef[BusinessRelationColId.RATE_TYPE],
      BusinessRelationColumnDef[BusinessRelationColId.RATE_VALUE],
      BusinessRelationColumnDef[BusinessRelationColId.SALE_TYPE],
      BusinessRelationColumnDef[BusinessRelationColId.SPECIES],
      BusinessRelationColumnDef[BusinessRelationColId.SALE_SUB_TYPE],
      BusinessRelationColumnDef[BusinessRelationColId.LONG_DESCRIPTION],
      BusinessRelationColumnDef[BusinessRelationColId.HASH_MODAL_ACTIONS],
    ],
  },
};

const columnDefs: (ColDef<BusinessRelationAgGridRowType> | ColGroupDef)[] = [
  LocalBusinessRelationColumnDef[BusinessRelationColId.RELATED_FROM_BUSINESS],
  LocalBusinessRelationColumnDef[BusinessRelationColId.RELATED_TO_BUSINESS],
  LocalBusinessRelationColumnDef[BusinessRelationColId.BUSINESS_RELATION],
];

export function BusinessRelationsTable(): React.JSX.Element {
  const rowData = useSelector(selectBusinessRelationsAggridData);
  const hasExportCsvPermissions = useHasExportBusinessesPermission();

  const hasAddPermissions = useHasDeploymentPermission(
    DeploymentPermissions.canAddBusinessRelation,
  );

  const additionalActions = useMemo(
    () =>
      [
        hasAddPermissions && {
          default: true,
          title: "Add Relationship",
          isDisabled: false,
          onClick: () => openBusinessRelationshipHashRoute(),
          icon: faPlus,
          dataTour: "addNewRelationship",
        },
      ].filter(Boolean),
    [hasAddPermissions],
  );

  return (
    <Column fullHeight>
      <AgGridTable
        additionalActions={additionalActions}
        columnDefs={columnDefs}
        context={{}}
        getRowId={getRowId}
        rowData={rowData}
        tableName={AgGridTables.BUSINESS_RELATIONSHIPS_MANAGE}
        hasExportCsvPermissions={hasExportCsvPermissions}
      />
    </Column>
  );
}
